/**
 * Fee Settings — barrister settings
 * Route: /barrister/settings/fees
 */
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { toast } from 'sonner';
import {
  CurrencyGbp, Percent, Handshake,
  Warning, CheckCircle, CircleNotch,
} from '@phosphor-icons/react';
import { api } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────

interface FeeSettings {
  hourlyRate: number | null;
  briefFeeMin: number | null;
  refresherRate: number | null;
  vatRegistered: boolean;
  vatNumber: string | null;
  acceptCfa: boolean;
  acceptDba: boolean;
  acceptDirectAccess: boolean;
}

const DEFAULT: FeeSettings = {
  hourlyRate: null,
  briefFeeMin: null,
  refresherRate: null,
  vatRegistered: false,
  vatNumber: null,
  acceptCfa: false,
  acceptDba: false,
  acceptDirectAccess: false,
};

// ─── API helpers ──────────────────────────────────────────────

async function fetchFeeSettings(): Promise<FeeSettings> {
  const res = await api.get('/me/fee-settings');
  return res.data.data;
}

async function saveFeeSettings(data: FeeSettings): Promise<FeeSettings> {
  const res = await api.patch('/me/fee-settings', data);
  return res.data.data;
}

// ─── Shared primitives ────────────────────────────────────────

const inputCls =
  'w-full rounded-xl border border-border/70 bg-background px-3.5 py-2.5 text-sm text-foreground focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function SectionHead({ icon, title, desc }: { icon: React.ReactNode; title: string; desc?: string }) {
  return (
    <div className="mb-5 flex items-start gap-3 border-b border-border/50 pb-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:text-brand-400">
        {icon}
      </div>
      <div>
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {desc && <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>}
      </div>
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors duration-150 ${
        value ? 'bg-brand-600' : 'bg-muted'
      }`}
      role="switch"
      aria-checked={value}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all duration-150 ${
          value ? 'left-4' : 'left-0.5'
        }`}
      />
    </button>
  );
}

// ── Number input helpers ──────────────────────────────────────

function numToStr(v: number | null): string {
  return v === null || v === undefined ? '' : String(v);
}
function strToNum(s: string): number | null {
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

export default function FeeSettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: fetched, isLoading } = useQuery({
    queryKey: ['me-fee-settings'],
    queryFn: fetchFeeSettings,
    staleTime: 0,
  });

  const [form, setForm] = useState<FeeSettings>(DEFAULT);
  const [saved, setSaved] = useState<FeeSettings>(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!fetched) return;
    setForm(fetched);
    setSaved(fetched);
  }, [fetched]);

  const isDirty = useMemo(() => {
    const keys = Object.keys(form) as Array<keyof FeeSettings>;
    return keys.some(k => form[k] !== saved[k]);
  }, [form, saved]);

  const set = <K extends keyof FeeSettings>(key: K) => (value: FeeSettings[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      const updated = await saveFeeSettings(form);
      setSaved(updated);
      setSavedAt(new Date());
      qc.setQueryData(['me-fee-settings'], updated);
      toast.success('Fee settings saved');
    } catch (err: any) {
      // The server now refuses a negative rate or a VAT number that is not shaped like one,
      // and it says which. Swallowing that for a generic "Failed to save" left the user with
      // a refusal and no idea what to change.
      toast.error(err?.response?.data?.error?.message ?? 'Failed to save fee settings');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch size={22} className="animate-spin text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl pb-20">
      {/* Reached from Settings but routed as a page of its own, so it inherits no back
          control from the Settings shell. History, not a fixed destination: the same page is
          opened from more than one place. */}
      <BackButton onClick={() => navigate(-1)} />

      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Fees &amp; VAT</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Your rate card, VAT registration, and instruction types accepted.
        </p>
      </div>

      <div className="space-y-6">

        {/* Section A — Rate Card */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<CurrencyGbp size={14} weight="bold" />}
            title="Rate Card"
            desc="Default rates applied when generating fee notes"
          />
          <div className="space-y-4">
            <Field label="Hourly Rate (£/hr)" hint="Used when generating fee notes">
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  £
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={numToStr(form.hourlyRate)}
                  onChange={e => set('hourlyRate')(strToNum(e.target.value))}
                  placeholder="e.g. 500"
                  className={`${inputCls} pl-7`}
                />
              </div>
            </Field>

            <Field label="Brief Fee Minimum (£)" hint="Used when generating fee notes">
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  £
                </span>
                <input
                  type="number"
                  min={0}
                  step={100}
                  value={numToStr(form.briefFeeMin)}
                  onChange={e => set('briefFeeMin')(strToNum(e.target.value))}
                  placeholder="e.g. 10000"
                  className={`${inputCls} pl-7`}
                />
              </div>
            </Field>

            <Field label="Refresher Rate (£/day)" hint="Used when generating fee notes">
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  £
                </span>
                <input
                  type="number"
                  min={0}
                  step={100}
                  value={numToStr(form.refresherRate)}
                  onChange={e => set('refresherRate')(strToNum(e.target.value))}
                  placeholder="e.g. 3500"
                  className={`${inputCls} pl-7`}
                />
              </div>
            </Field>
          </div>
        </div>

        {/* Section B — VAT */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<Percent size={14} weight="bold" />}
            title="VAT"
            desc="Your VAT number appears on all fee notes you issue"
          />

          <div className="space-y-4">
            {/* VAT registered toggle */}
            <div className="flex items-center justify-between rounded-xl border border-border/50 px-4 py-3.5">
              <div>
                <div className="text-sm font-semibold text-foreground">VAT Registered</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Enable if you are registered for VAT with HMRC
                </div>
              </div>
              <Toggle value={form.vatRegistered} onChange={set('vatRegistered')} />
            </div>

            {/* VAT number — conditional */}
            {form.vatRegistered && (
              <Field label="VAT Number" hint="Your VAT number appears on all fee notes">
                <input
                  type="text"
                  value={form.vatNumber ?? ''}
                  onChange={e => set('vatNumber')(e.target.value || null)}
                  placeholder="GB123456789"
                  className={inputCls}
                />
              </Field>
            )}
          </div>
        </div>

        {/* Section C — Instructions Accepted */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<Handshake size={14} weight="bold" />}
            title="Instructions Accepted"
            desc="Displayed on your public profile to instructing solicitors"
          />

          <div className="space-y-2.5">
            {[
              {
                key: 'acceptCfa' as const,
                label: 'Conditional Fee Agreements (CFA)',
                desc: 'No-win-no-fee arrangements',
              },
              {
                key: 'acceptDba' as const,
                label: 'Damages-Based Agreements (DBA)',
                desc: 'Fee as a percentage of damages recovered',
              },
              {
                key: 'acceptDirectAccess' as const,
                label: 'Direct Access (Public Access)',
                desc: 'Instructions received directly from lay clients',
              },
            ].map(item => (
              <label
                key={item.key}
                className="flex cursor-pointer items-start gap-3.5 rounded-xl border border-border/50 px-4 py-3.5 transition hover:bg-muted/20"
              >
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={form[item.key]}
                    onChange={e => set(item.key)(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">{item.label}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{item.desc}</div>
                </div>
              </label>
            ))}
          </div>

          <p className="mt-3.5 text-[10px] text-muted-foreground/70">
            Displayed on your public profile to instructing solicitors
          </p>
        </div>

      </div>

      {/*
        No panel, no pinning — just the two controls at the end of the form.
        The bar was a full-width card that floated over the VAT panel on a wide screen and sat
        mostly empty on a narrow one, since its status text only appears once something is edited.
      */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-xs">
            {isDirty && (
              <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                <Warning size={12} weight="fill" />
                Unsaved changes
              </span>
            )}
            {savedAt && !isDirty && (
              <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                <CheckCircle size={12} weight="fill" />
                Saved {savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className={`flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-semibold transition-all ${
                isDirty
                  ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
            >
              {saving ? (
                <><CircleNotch size={13} className="animate-spin" />Saving…</>
              ) : (
                'Save changes'
              )}
            </button>
            {/* Always present, disabled until there is something to discard. Order is Save
                then Cancel on request. */}
            <button
              type="button"
              onClick={() => setForm(saved)}
              disabled={!isDirty || saving}
              className="h-9 rounded-lg border border-border px-4 text-sm font-semibold text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
      </div>
    </div>
  );
}
